import React, { useState, useRef, useEffect, useCallback } from "react";
import { IoClose, IoAdd } from "react-icons/io5";
import styles from "../ResumePage.module.css";

export const KeywordsSection = ({
  keywords = [],
  isLoading = false,
  onAddKeyword,
  onRemoveKeyword,
  onApply,
  hasUnappliedChanges = false,
  disabled = false,
}) => {
  const [showAddInput, setShowAddInput] = useState(false);
  const [newKeyword, setNewKeyword] = useState("");
  const [error, setError] = useState("");
  const [removingKeyword, setRemovingKeyword] = useState(null);

  const inputRef = useRef(null);
  const addButtonRef = useRef(null);

  // Focus input when showing
  useEffect(() => {
    if (showAddInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showAddInput]);

  // Clear error after timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const resetInput = useCallback(() => {
    setNewKeyword("");
    setShowAddInput(false);
    setError("");
  }, []);

  const handleAddKeyword = useCallback(async () => {
    const trimmedKeyword = newKeyword.trim();

    if (!trimmedKeyword) {
      setError("Keyword cannot be empty");
      return;
    }

    // Check for duplicates (case-insensitive)
    const isDuplicate = keywords.some(
      (k) => k.toLowerCase() === trimmedKeyword.toLowerCase()
    );
    if (isDuplicate) {
      setError("Keyword already exists");
      return;
    }

    try {
      const result = await onAddKeyword?.(trimmedKeyword);
      if (result !== false) {
        resetInput();
      }
    } catch (err) {
      setError(err?.message || "Failed to add keyword");
    }
  }, [newKeyword, keywords, onAddKeyword, resetInput]);

  const handleRemoveKeyword = useCallback(
    async (keyword) => {
      if (disabled || isLoading) return;

      setRemovingKeyword(keyword);

      try {
        await onRemoveKeyword?.(keyword);
      } catch (err) {
        setError(err?.message || "Failed to remove keyword");
      } finally {
        setTimeout(() => setRemovingKeyword(null), 150);
      }
    },
    [disabled, isLoading, onRemoveKeyword]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddKeyword();
      } else if (e.key === "Escape") {
        e.preventDefault();
        resetInput();
        addButtonRef.current?.focus();
      }
    },
    [handleAddKeyword, resetInput]
  );

  const handleInputChange = useCallback(
    (e) => {
      setNewKeyword(e.target.value);
      if (error) setError("");
    },
    [error]
  );

  const handleCancel = useCallback(() => {
    resetInput();
    addButtonRef.current?.focus();
  }, [resetInput]);

  const handleApply = useCallback(async () => {
    if (keywords.length === 0 || isLoading || disabled) return;

    try {
      await onApply?.();
    } catch (err) {
      setError(err?.message || "Failed to apply keywords");
    }
  }, [keywords.length, isLoading, disabled, onApply]);

  const isApplyDisabled = keywords.length === 0 || isLoading || disabled;

  return (
    <>
      <div
        className={styles.keywordsContainer}
        role="region"
        aria-label="Keywords list"
      >
        {keywords.length === 0 ? (
          <div className={styles.noKeywords} role="status">
            <p>No keywords extracted. Add keywords manually below.</p>
          </div>
        ) : (
          <div className={styles.keywordsList} role="list">
            {keywords.map((keyword, index) => (
              <span
                key={`${keyword}-${index}`}
                className={`${styles.keywordTag} ${
                  removingKeyword === keyword ? styles.keywordRemoving : ""
                }`}
                role="listitem"
              >
                {keyword}
                <button
                  type="button"
                  className={styles.removeKeywordButton}
                  onClick={() => handleRemoveKeyword(keyword)}
                  disabled={disabled || isLoading}
                  aria-label={`Remove keyword: ${keyword}`}
                >
                  <IoClose aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={styles.addKeywordSection}>
        {error && (
          <div className={styles.keywordError} role="alert" aria-live="assertive">
            {error}
          </div>
        )}

        <div className={styles.keywordActionsRow}>
          {showAddInput ? (
            <div className={styles.addKeywordInputWrapper}>
              <input
                ref={inputRef}
                type="text"
                className={`${styles.addKeywordInput} ${
                  error ? styles.inputError : ""
                }`}
                placeholder="Enter new keyword"
                value={newKeyword}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                aria-label="New keyword"
                aria-invalid={!!error}
              />
              <button
                type="button"
                className={styles.addKeywordConfirmButton}
                onClick={handleAddKeyword}
                disabled={!newKeyword.trim() || disabled}
                aria-label="Confirm add keyword"
              >
                Add
              </button>
              <button
                type="button"
                className={styles.addKeywordCancelButton}
                onClick={handleCancel}
                aria-label="Cancel adding keyword"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              ref={addButtonRef}
              type="button"
              className={styles.addKeywordButton}
              onClick={() => setShowAddInput(true)}
              disabled={disabled}
              aria-label="Add new keyword"
            >
              <IoAdd aria-hidden="true" />
              <span>Add Keyword</span>
            </button>
          )}

          <div className={styles.applyWrapper}>
            <button
              type="button"
              className={styles.agenticButton}
              onClick={handleApply}
              disabled={isApplyDisabled}
              aria-busy={isLoading}
            >
              {isLoading ? "Searching..." : "Apply"}
            </button>

            {hasUnappliedChanges && !isLoading && keywords.length > 0 && (
              <p className={styles.keywordsChangedHint} role="status" aria-live="polite">
                Keywords have changed. Click Apply to search.
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default KeywordsSection;